// Fase 17.3, Plano 06 (D-19, D-20, D-21) — dados técnicos da peça na própria ficha do roteiro.
// Peça sem área ou sem cor continua com roteiro válido. Uma tinta elegível definida na peça dispensa a cor para calcular o consumo.
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ConsumoTintaEstimadoDto, DadosTecnicosPecaDto, FichaPecaRoteiroDto } from '@pieltec/contracts';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { CampoSelect } from '../../../components/ui/campo-select';
import { Input } from '../../../components/ui/input';
import {
  alteracoesDosDados,
  camposPendentesConsumo,
  formDosDados,
  mesclarFormComRecarga,
  opcoesIsolamento,
  TEXTO_CONSUMO_A_DEFINIR,
  TEXTO_ROTEIRO_SEM_ETAPA_DE_PINTURA,
  textoConsumoDeTinta,
  textoConsumoPrevistoPorPeca,
  validarDadosTecnicos,
  type DadosTecnicosForm,
} from '../../../lib/roteiros/dados-tecnicos';
import { opcoesBanho, opcoesFamiliaCor, opcoesPorte, opcoesTinta } from '../../../lib/material-atributos';
import { avisosLegiveis } from '../../../lib/roteiros/consumo-tinta';
import { atualizarDadosTecnicos, ErroRoteiroApi, ignorarCorSugerida } from '../../../lib/roteiros/roteiros-api';

export interface DadosTecnicosFormProps {
  readonly materialId: string;
  readonly dados: DadosTecnicosPecaDto;
  /** Sem roteiros:gerenciar a ficha é só leitura (D-16). */
  readonly somenteLeitura: boolean;
  /** Chamado com os dados salvos para a ficha atualizar o aviso de dados incompletos. */
  readonly onSalvo?: (dados: DadosTecnicosPecaDto) => void;
  /** Estimativa do servidor (D-21); sem ela a ficha mostra o texto genérico do plano 06. */
  readonly consumoEstimado?: ConsumoTintaEstimadoDto | null;
  /** D-08: cor sugerida pela descrição do CargaMáquina; só vem enquanto a peça está sem cor. */
  readonly corSugerida?: FichaPecaRoteiroDto['corSugerida'];
}

const rotuloDe = (opcoes: readonly { value: string; label: string }[], valor: string): string => opcoes.find((o) => o.value === valor)?.label ?? 'Não informado';

/** Resumo em texto (leitura) — também usado quando o perfil não pode editar. */
export function DadosTecnicosLeitura({ dados }: { readonly dados: DadosTecnicosPecaDto }) {
  const form = formDosDados(dados);
  const linhas: [string, string][] = [
    ['Área (m²)', form.area || 'Não informada'],
    ['Cor (código)', form.corCodigo || 'Não informada'],
    ['Família da cor', rotuloDe(opcoesFamiliaCor, form.corFamilia)],
    ['Tipo de tinta', rotuloDe(opcoesTinta, form.tipoTinta)],
    ['Peças por gancheira', form.pecasPorGancheira || 'Não informado'],
    ['Exige isolamento', rotuloDe(opcoesIsolamento, form.exigeIsolamento)],
    ['Porte', rotuloDe(opcoesPorte, form.porte)],
    ['Tipo de banho', rotuloDe(opcoesBanho, form.tipoBanho)],
  ];
  return (
    <dl className="grid gap-4 md:grid-cols-2 lg:grid-cols-4" aria-label="Dados técnicos da peça">
      {linhas.map(([rotulo, valor]) => (
        <div key={rotulo} className="flex flex-col gap-1">
          <dt className="text-xs text-muted-foreground">{rotulo}</dt>
          <dd className="text-sm">{valor}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * D-21: com a estimativa do servidor mostra o consumo previsto na unidade da tinta (com o código da tinta) ou, se ainda falta dado,
 * "Consumo de tinta: a definir" com os motivos em português. Sem estimativa mantém o texto genérico (dados incompletos / na liberação).
 */
export function ResumoConsumoDeTinta({ dados, estimado, mostrarPendencias = true }: { readonly dados: DadosTecnicosPecaDto; readonly estimado?: ConsumoTintaEstimadoDto | null; readonly mostrarPendencias?: boolean }) {
  const usaTintaDaPeca = estimado?.origemTinta === 'PECA';
  const pendentes = camposPendentesConsumo(dados, usaTintaDaPeca);
  const incompletos = mostrarPendencias && pendentes.length > 0 ? <p role="status" className="text-sm font-semibold">Falta informar para estimar o consumo de tinta: {pendentes.map((campo) => campo.rotulo).join(' e ')}.</p> : null;
  if (estimado?.situacao === 'CALCULADO' && estimado.kgPorPeca !== null) {
    return (
      <div className="flex flex-col gap-1">
        {incompletos}
        <p className="text-sm text-muted-foreground">
          {textoConsumoPrevistoPorPeca(estimado)}
        </p>
      </div>
    );
  }
  if (estimado) {
    const motivos = avisosLegiveis(estimado.avisos);
    return (
      <div className="flex flex-col gap-1">
        {incompletos}
        <p className="text-sm text-muted-foreground">{TEXTO_CONSUMO_A_DEFINIR}</p>
        {motivos.length === 0 ? <p className="text-xs text-muted-foreground">{TEXTO_ROTEIRO_SEM_ETAPA_DE_PINTURA}</p> : (
          <ul className="flex flex-col gap-1 text-xs text-muted-foreground">{motivos.map((motivo) => <li key={motivo}>{motivo}</li>)}</ul>
        )}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      {incompletos}
      <p className="text-sm text-muted-foreground">{textoConsumoDeTinta(dados)}</p>
    </div>
  );
}

/**
 * D-08: o PCP confirma a cor sugerida (grava corCodigo pelo PATCH de dados técnicos) ou a ignora. Sem roteiros:gerenciar,
 * só o texto. O trecho da descrição é texto puro (React escapa), limitado a 200 caracteres pela API.
 */
export function CorSugeridaAviso({ materialId, sugestao, somenteLeitura, onResolvida }: {
  readonly materialId: string;
  readonly sugestao: NonNullable<FichaPecaRoteiroDto['corSugerida']>;
  readonly somenteLeitura: boolean;
  readonly onResolvida?: () => void;
}) {
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const resolver = async (acao: () => Promise<unknown>): Promise<void> => {
    setOcupado(true);
    setErro(null);
    try {
      await acao();
      onResolvida?.();
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível concluir. Tente novamente.');
    } finally {
      setOcupado(false);
    }
  };
  return (
    <div className="flex flex-col gap-2 rounded-md border p-4" role="group" aria-label="Cor sugerida">
      <p className="text-sm font-semibold">{`Cor sugerida pelo CargaMáquina: ${sugestao.cor}`}</p>
      {sugestao.trechoDescricao ? <p className="text-xs text-muted-foreground">{`Descrição de origem: ${sugestao.trechoDescricao}`}</p> : null}
      {somenteLeitura ? null : (
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" disabled={ocupado} onClick={() => void resolver(() => atualizarDadosTecnicos(materialId, { corCodigo: sugestao.cor }))}>Confirmar cor</Button>
          <Button type="button" variant="outline" disabled={ocupado} onClick={() => void resolver(() => ignorarCorSugerida(materialId))}>Ignorar</Button>
        </div>
      )}
      {erro ? <p role="alert" className="text-sm text-destructive">{erro}</p> : null}
    </div>
  );
}

export function DadosTecnicosForm({ materialId, dados, somenteLeitura, onSalvo, consumoEstimado, corSugerida }: DadosTecnicosFormProps) {
  const inicial = useMemo(() => formDosDados(dados), [dados]);
  const [form, setForm] = useState<DadosTecnicosForm>(inicial);
  const [mostrarErros, setMostrarErros] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);

  // Recarga da ficha (ex.: "Confirmar cor") não apaga o que foi digitado e ainda não salvo (P3-WR-06).
  const salvoAnterior = useRef(inicial);
  useEffect(() => {
    const anterior = salvoAnterior.current;
    salvoAnterior.current = inicial;
    setForm((atual) => mesclarFormComRecarga(atual, anterior, inicial));
  }, [inicial]);

  const erros = validarDadosTecnicos(form);
  const alteracoes = alteracoesDosDados(inicial, form);
  const temMudanca = Object.keys(alteracoes).length > 0;
  const pendentes = camposPendentesConsumo({ areaUnitariaM2: form.area, corCodigo: form.corCodigo }, consumoEstimado?.origemTinta === 'PECA');
  const outrosNaoInformados = [
    [form.corFamilia, 'Família da cor'], [form.tipoTinta, 'Tipo de tinta'],
    [form.pecasPorGancheira, 'Peças por gancheira'], [form.exigeIsolamento, 'Exige isolamento'],
    [form.porte, 'Porte'], [form.tipoBanho, 'Tipo de banho'],
  ].filter(([valor]) => !valor?.trim()).map(([, rotulo]) => rotulo);
  const mudar = (parcial: Partial<DadosTecnicosForm>): void => { setForm((atual) => ({ ...atual, ...parcial })); setSucesso(null); };

  const avisoCor = corSugerida ? <CorSugeridaAviso materialId={materialId} sugestao={corSugerida} somenteLeitura={somenteLeitura} onResolvida={() => onSalvo?.(dados)} /> : null;

  if (somenteLeitura) {
    return (
      <div className="flex flex-col gap-4">
        {avisoCor}
        <DadosTecnicosLeitura dados={dados} />
        <ResumoConsumoDeTinta dados={dados} estimado={consumoEstimado} />
      </div>
    );
  }

  const salvar = async (): Promise<void> => {
    if (Object.keys(erros).length > 0) { setMostrarErros(true); return; }
    setSalvando(true);
    setErro(null);
    try {
      const salvos = await atualizarDadosTecnicos(materialId, alteracoes);
      setSucesso('Dados técnicos salvos.');
      setMostrarErros(false);
      onSalvo?.(salvos);
    } catch (falha) {
      setErro(falha instanceof ErroRoteiroApi || falha instanceof Error ? falha.message : 'Não foi possível salvar os dados técnicos. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {avisoCor}
      <div className="space-y-1 text-sm" aria-live="polite" aria-atomic="true">
        {pendentes.length > 0 ? <><p className="font-semibold">Falta informar para estimar o consumo de tinta:</p><ul className="flex flex-wrap gap-x-4">{pendentes.map((campo) => <li key={campo.id}><button type="button" className="inline-flex min-h-11 items-center underline underline-offset-4" onClick={() => document.getElementById(campo.id)?.focus()}>Preencher {campo.rotulo}</button></li>)}</ul><p className="text-muted-foreground">Você pode salvar a ficha com esses dados pendentes; o consumo ficará a definir.</p></> : temMudanca ? <p>Campos para o consumo preenchidos. Salve os dados técnicos para atualizar a ficha e a estimativa.</p> : null}
        {outrosNaoInformados.length > 0 ? <p className="text-muted-foreground">Outros dados não informados: {outrosNaoInformados.join(', ')}. Preencha conforme o processo; eles não são o motivo do aviso de área e cor.</p> : null}
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div className="flex flex-col gap-1">
          <label htmlFor="dados-area" className="text-sm font-semibold">Área (m²)</label>
          <Input id="dados-area" inputMode="decimal" placeholder="Ex.: 0,5" value={form.area} aria-invalid={mostrarErros && erros.area !== undefined} onChange={(evento) => mudar({ area: evento.target.value })} />
          {pendentes.some((campo) => campo.id === 'dados-area') ? <p className="text-xs font-semibold">Área ainda não informada para o consumo.</p> : null}
          {mostrarErros && erros.area ? <p role="alert" className="text-xs text-destructive">{erros.area}</p> : null}
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="dados-cor" className="text-sm font-semibold">Cor (código)</label>
          <Input id="dados-cor" maxLength={40} value={form.corCodigo} onChange={(evento) => mudar({ corCodigo: evento.target.value })} />
          {pendentes.some((campo) => campo.id === 'dados-cor') ? <p className="text-xs font-semibold">Cor ainda não informada para o consumo.</p> : null}
          {mostrarErros && erros.corCodigo ? <p role="alert" className="text-xs text-destructive">{erros.corCodigo}</p> : null}
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="dados-familia" className="text-sm font-semibold">Família da cor</label>
          <CampoSelect id="dados-familia" value={form.corFamilia} placeholder="Não informada" options={[{ value: '', label: 'Não informada' }, ...opcoesFamiliaCor]} onValueChange={(valor) => mudar({ corFamilia: valor })} />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="dados-tinta" className="text-sm font-semibold">Tipo de tinta</label>
          <CampoSelect id="dados-tinta" value={form.tipoTinta} placeholder="Não informado" options={[{ value: '', label: 'Não informado' }, ...opcoesTinta]} onValueChange={(valor) => mudar({ tipoTinta: valor })} />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="dados-gancheira" className="text-sm font-semibold">Peças por gancheira</label>
          <Input id="dados-gancheira" inputMode="numeric" value={form.pecasPorGancheira} aria-invalid={mostrarErros && erros.pecasPorGancheira !== undefined} onChange={(evento) => mudar({ pecasPorGancheira: evento.target.value })} />
          {mostrarErros && erros.pecasPorGancheira ? <p role="alert" className="text-xs text-destructive">{erros.pecasPorGancheira}</p> : null}
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="dados-isolamento" className="text-sm font-semibold">Exige isolamento</label>
          <CampoSelect id="dados-isolamento" value={form.exigeIsolamento} placeholder="Não informado" options={opcoesIsolamento} onValueChange={(valor) => mudar({ exigeIsolamento: valor })} />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="dados-porte" className="text-sm font-semibold">Porte</label>
          <CampoSelect id="dados-porte" value={form.porte} placeholder="Não informado" options={[{ value: '', label: 'Não informado' }, ...opcoesPorte]} onValueChange={(valor) => mudar({ porte: valor })} />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="dados-banho" className="text-sm font-semibold">Tipo de banho</label>
          <CampoSelect id="dados-banho" value={form.tipoBanho} placeholder="Não informado" options={[{ value: '', label: 'Não informado' }, ...opcoesBanho]} onValueChange={(valor) => mudar({ tipoBanho: valor })} />
        </div>
      </div>
      <div><p className="text-xs text-muted-foreground">Estimativa com os dados salvos</p><ResumoConsumoDeTinta dados={dados} estimado={consumoEstimado} mostrarPendencias={false} /></div>
      {erro ? <p role="alert" className="text-sm text-destructive">{erro}</p> : null}
      {sucesso ? <p role="status" className="text-sm font-semibold">{sucesso}</p> : null}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" disabled={salvando || !temMudanca} onClick={() => void salvar()}>{salvando ? 'Salvando...' : 'Salvar dados técnicos'}</Button>
        {temMudanca ? <Badge variant="outline">Alterações não salvas</Badge> : null}
      </div>
    </div>
  );
}
